import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { InfoRounded as InfoRoundedIcon } from '@mui/icons-material';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import DialogWithContent from '@src/components/DialogWithContent';
import PaperFireworks from '@src/assets/images/paper-fireworks.svg';
import ReferralErrorIcon from '@src/assets/images/referral-error-noti.svg';
import GiftIcon from '@src/assets/icons/gift-filled-icon.svg';
import { delimitNumber } from '@src/utils/number';
import routes from '@src/constants/route';
import actions from '@src/redux/actions';
import {
  MAX_ALLOWED_REDEEM_CHARACTERS_PER_MONTH,
  REFERRAL_CHARACTER_REWARD_STEP,
} from '@src/constants/referral';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { redeemReferral } from '@src/apis/referral';
import theme from '@src/styles/theme';
import i18n from '@src/languages';
import { StyledReferralDialogNote } from '../index.style';
import {
  StyledReferralRedeemContent,
  StyledReferralCannotRedeemContent,
} from './index.style';

const GiftButton = ({ maxRewardCharacter, redeemedCharacters }) => {
  const { getFeatureValue } = useFeatureFlags();
  const { t } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { referral, packageExpiryDate } = user;
  const { earnedPoints = 0, spentPoints = 0 } = referral || {};
  const initPoints = earnedPoints - spentPoints;

  const [value, setValue] = useState(maxRewardCharacter);
  const [openPopupReward, setOpenPopupReward] = useState(false);
  const [openPopupCannotRedeem, setOpenPopupCannotRedeem] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isLoading, setIsLoading] = useState(false);

  const limitRedeemCharacterEnabled = getFeatureValue(
    FEATURE_KEYS.LIMIT_REDEEM_CHARACTER_PER_MONTH,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const hasReachedMaxRedeemCharacter = limitRedeemCharacterEnabled
    ? redeemedCharacters === MAX_ALLOWED_REDEEM_CHARACTERS_PER_MONTH
    : false;

  const handleDecrease = () => {
    if (value >= REFERRAL_CHARACTER_REWARD_STEP)
      setValue(value - REFERRAL_CHARACTER_REWARD_STEP);
  };

  const handleIncrease = () => setValue(value + REFERRAL_CHARACTER_REWARD_STEP);

  const handleClosePopupReward = () => {
    setOpenPopupReward(false);
    setValue(maxRewardCharacter);
  };

  const handleClosePopupCannotRedeem = () => setOpenPopupCannotRedeem(false);

  const handleClickGiftButton = () => {
    if (packageExpiryDate && moment().isAfter(packageExpiryDate))
      setOpenPopupCannotRedeem(true);
    else setOpenPopupReward(true);
  };

  const handleClickButtonBuyNow = () => {
    history.push(routes.PAYMENT);
    handleClosePopupCannotRedeem();
  };

  const handleRedeem = async () => {
    const points = value / REFERRAL_CHARACTER_REWARD_STEP;
    setIsLoading(true);
    const response = await redeemReferral(points);
    setIsLoading(false);
    const { status } = response || {};
    if (status) {
      setOpenPopupReward(false);
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorOccurred'),
        }),
      );
    }
  };

  const renderRedeemAction = () => (
    <Button
      onClick={handleRedeem}
      variant="contained"
      className="dialog-actions-button"
      disabled={isLoading || !value}
      sx={{
        opacity: isLoading || !value ? 0.5 : 1,
        color: 'inherit',
        backgroundColor: 'primary.main',
        '&:disabled': {
          color: 'inherit',
          backgroundColor: 'primary.main',
        },
      }}
    >
      {t('claimRewards')}
    </Button>
  );

  const renderCannotRedeemAction = () => (
    <Button
      variant="contained"
      className="dialog-actions-button"
      onClick={handleClickButtonBuyNow}
    >
      {t('buyNow')}
    </Button>
  );

  const contentRewardDialog = () => (
    <StyledReferralRedeemContent
      hasReachedMaxRedeemCharacter={hasReachedMaxRedeemCharacter}
    >
      <Box className="redeem-character-wrapper">
        <Box className="character-counter-wrapper">
          <IconButton
            disabled={value === 0}
            className="decrease-button"
            onClick={handleDecrease}
          >
            <RemoveRoundedIcon color={value === 0 ? 'disabled' : 'inherit'} />
          </IconButton>
          <Box className="character-counter">
            <Typography className="character-counter-text">
              {delimitNumber(value, language)}
            </Typography>
            <Typography className="character-text-lower">
              {t('charactersLower')}
            </Typography>
          </Box>
          <IconButton
            disabled={value === maxRewardCharacter}
            className="increase-button"
            onClick={handleIncrease}
          >
            <AddRoundedIcon
              color={value === maxRewardCharacter ? 'disabled' : 'inherit'}
            />
          </IconButton>
        </Box>
        {limitRedeemCharacterEnabled && (
          <Box className="redeemed-character-wrapper">
            <Typography className="redeemed-character-text">
              {t('redeemedCharacters')}
            </Typography>
            <Typography className="redeemed-character-text">
              {delimitNumber(redeemedCharacters, language)}
            </Typography>
          </Box>
        )}
      </Box>

      <StyledReferralDialogNote>
        <InfoRoundedIcon className="note-icon" />
        {limitRedeemCharacterEnabled ? (
          <Typography
            className="note-text"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('rewardFromReferralCodeNote', {
                  maxAllowedRedeemCharacters: delimitNumber(
                    MAX_ALLOWED_REDEEM_CHARACTERS_PER_MONTH,
                    language,
                  ),
                }),
              ),
            }}
          />
        ) : (
          <Typography className="note-text">
            {t('rewardFromReferralCodeInfo')}
          </Typography>
        )}
      </StyledReferralDialogNote>
    </StyledReferralRedeemContent>
  );

  const contentCannotRedeemDialog = () => (
    <StyledReferralCannotRedeemContent>
      <Typography
        className="cannot-redeem-text"
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(t('cannotRedeemReferralRewardDesc')),
        }}
      />
    </StyledReferralCannotRedeemContent>
  );

  useEffect(() => {
    setValue(maxRewardCharacter);
  }, [maxRewardCharacter]);

  return (
    <>
      <Box
        className="gift-button"
        variant="contained"
        onClick={handleClickGiftButton}
      >
        {initPoints > 0 && <Box className="dot-of-gift" />}
        <img src={GiftIcon} alt="gift-icon" className="gift-icon" />
        {isMobile && (
          <Typography className="gift-text">{t('claimRewards')}</Typography>
        )}
      </Box>
      <DialogWithContent
        open={openPopupReward}
        onClose={handleClosePopupReward}
        contentPopup={contentRewardDialog()}
        image={PaperFireworks}
        title={t('claimCharacterRewardsTitle')}
        actionComponent={renderRedeemAction()}
      />
      <DialogWithContent
        open={openPopupCannotRedeem}
        onClose={handleClosePopupCannotRedeem}
        contentPopup={contentCannotRedeemDialog()}
        title={t('cannotRedeemReferralRewardTitle')}
        image={ReferralErrorIcon}
        actionComponent={renderCannotRedeemAction()}
      />
    </>
  );
};

export default GiftButton;
